/**
 * templateCtaAnalytics — tracking unificado de vistas y CTAs para los templates
 * que NO usaban el mirror de funnel (ArgentinaBio, BioLink, Sorprendeme,
 * BestFriendInstagram, BaseTemplateInternational, footer, …).
 *
 * Antes estos templates solo disparaban PostHog (o nada). No llegaban a
 * `/api/track-funnel-event` → ni a `funnel_events` ni a Slack #anaafernandez.
 * Este helper espeja cada acción al mismo pipeline que ya usa BaseTemplate,
 * de modo que la enriquecimiento de origen (traffic_source first-touch que
 * inyecta mirrorFunnelEvent) aplica también acá.
 *
 * Convención de nombres de evento (registrar en sendSlackFunnelNotification.ts):
 *   <prefix>_view                — vista del template (ONCE_PER_SESSION)
 *   <prefix>_<action>_click      — click de CTA (ALWAYS_NOTIFY)
 *   <prefix>_<action>            — paso de wizard / acción no-click (ALWAYS_NOTIFY)
 */

import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import {
  getBaseTemplatePosthogDistinctId,
  getBaseTemplateSessionId,
  workerGeoForBaseTemplateTracking,
} from "@/lib/baseTemplateAnalytics";

export type TemplateCtaExtra = Record<
  string,
  string | number | boolean | null | undefined
>;

function baseIds() {
  return {
    posthog_distinct_id: getBaseTemplatePosthogDistinctId(),
    session_id: getBaseTemplateSessionId(),
  };
}

/**
 * Vista de template. Se llama una vez al montar; el throttle once-per-session
 * vive en el servidor (sendSlackFunnelNotification), así que el cliente puede
 * dispararlo sin deduplicar.
 */
export function trackTemplateView(
  prefix: string,
  template: string,
  extra?: TemplateCtaExtra,
): void {
  if (typeof window === "undefined") return;
  const geo = workerGeoForBaseTemplateTracking();
  mirrorFunnelEvent({
    event_name: `${prefix}_view`,
    ...baseIds(),
    template,
    landing_path: window.location.pathname,
    country: (geo.country as string | undefined) ?? undefined,
    props: { ...geo, ...(extra ?? {}) },
  });
}

/**
 * Persistencia server-side en `template_views` (admin checkout-funnel top-of-funnel).
 * Best-effort, non-blocking. Misma forma que BioTemplatePremiumSimple.
 */
export function trackTemplateViewServer(body: {
  template: string;
  template_variant?: string;
  flow_name?: string;
  landing_path?: string;
  country?: string | null;
  language?: string | null;
  has_telegram_url?: boolean;
  has_membership_video?: boolean;
  has_exclusive_video?: boolean;
}): void {
  if (typeof window === "undefined") return;
  try {
    const phDistinctId = (
      window as unknown as { posthog?: { get_distinct_id?: () => string } }
    ).posthog?.get_distinct_id?.();
    void fetch("/api/track-template-view", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        template: body.template,
        template_variant: body.template_variant,
        flow_name: body.flow_name,
        landing_path:
          body.landing_path ??
          (typeof window !== "undefined" ? window.location.pathname : undefined),
        country: body.country ?? undefined,
        language: body.language ?? undefined,
        posthog_distinct_id: phDistinctId,
        has_telegram_url: body.has_telegram_url,
        has_membership_video: body.has_membership_video,
        has_exclusive_video: body.has_exclusive_video,
      }),
    }).catch(() => {});
  } catch {
    /* noop */
  }
}

/**
 * CTA / acción de un template. `action` debe ser snake_case corto
 * ("telegram", "instagram", "checkout_initiated", "amount_selected").
 * Si `isClick` (default true) el evento es `<prefix>_<action>_click`,
 * si no `<prefix>_<action>` (pasos de wizard, redirects).
 */
export function trackTemplateCta(params: {
  prefix: string;
  template: string;
  action: string;
  label?: string;
  href?: string;
  destination?: string;
  plan?: string;
  flowName?: string;
  isClick?: boolean;
  extra?: TemplateCtaExtra;
}): void {
  if (typeof window === "undefined") return;
  const geo = workerGeoForBaseTemplateTracking();
  const isClick = params.isClick !== false;
  const event_name = isClick
    ? `${params.prefix}_${params.action}_click`
    : `${params.prefix}_${params.action}`;

  mirrorFunnelEvent({
    event_name,
    ...baseIds(),
    template: params.template,
    flow_name: params.flowName,
    landing_path: window.location.pathname,
    country: (geo.country as string | undefined) ?? undefined,
    props: {
      ...geo,
      elementId: params.action,
      ...(params.label ? { label: params.label } : {}),
      ...(params.href ? { href: params.href } : {}),
      ...(params.destination ? { destination: params.destination } : {}),
      ...(params.plan ? { plan_tier: params.plan } : {}),
      ...(params.extra ?? {}),
    },
  });
}
